'use client';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  // Don't render if totalPages is invalid or <= 1
  if (
    typeof totalPages !== 'number' ||
    typeof currentPage !== 'number' ||
    typeof onPageChange !== 'function' ||
    totalPages <= 1 ||
    currentPage < 1 ||
    currentPage > totalPages
  ) {
    return null;
  }

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Near the start
        pages.push(2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push('...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // In the middle
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent, pageNum: number) => {
    if (e.key === 'ArrowLeft' && pageNum > 1) {
      e.preventDefault();
      onPageChange(pageNum - 1);
    } else if (e.key === 'ArrowRight' && pageNum < totalPages) {
      e.preventDefault();
      onPageChange(pageNum + 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onPageChange(1);
    } else if (e.key === 'End') {
      e.preventDefault();
      onPageChange(totalPages);
    }
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav className="flex items-center justify-center gap-2 mt-12" aria-label="Pagination">
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-surface text-foreground hover:bg-accent hover:text-accent-foreground disabled:hover:bg-surface disabled:hover:text-foreground"
        aria-label="Previous page"
      >
        ← Previous
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-1">
        {pageNumbers.map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-3 py-2 text-foreground-tertiary"
                aria-hidden="true"
              >
                ...
              </span>
            );
          }

          const pageNum = page as number;
          const isCurrent = pageNum === currentPage;

          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              onKeyDown={(e) => handleKeyDown(e, pageNum)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isCurrent
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-surface text-foreground hover:bg-accent-muted'
              }`}
              aria-label={`Page ${pageNum}`}
              aria-current={isCurrent ? 'page' : undefined}
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-surface text-foreground hover:bg-accent hover:text-accent-foreground disabled:hover:bg-surface disabled:hover:text-foreground"
        aria-label="Next page"
      >
        Next →
      </button>
    </nav>
  );
}
